import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isNavLinkActive } from './navPath';

export type NavDropdownLink = {
  to: string;
  label: string;
  description: string;
};

type NavDropdownProps = {
  label: string;
  links: readonly NavDropdownLink[];
  pathname: string;
  isActive: (pathname: string) => boolean;
};

const CLOSE_DELAY_MS = 120;

export function NavDropdown({ label, links, pathname, isActive }: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuId = useId();
  const active = isActive(pathname);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closeMenu = useCallback(() => {
    setOpen(false);
    const activeEl = document.activeElement;
    if (activeEl && rootRef.current?.contains(activeEl)) {
      triggerRef.current?.focus();
    }
  }, []);

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(closeMenu, CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) closeMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closeMenu]);

  return (
    <div
      ref={rootRef}
      className={`nav-dropdown${open ? ' is-open' : ''}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`nav-dropdown-trigger${active ? ' nav-dropdown-trigger--active' : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-current={active ? 'page' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <svg className="nav-dropdown-chevron" width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
          <path
            d="M1 2.5L4 5.5L7 2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div id={menuId} role="menu" className="nav-dropdown-panel" aria-hidden={!open}>
        {links.map((item) => (
          <Link
            key={item.to}
            role="menuitem"
            to={item.to}
            className="nav-dropdown-item"
            aria-current={isNavLinkActive(pathname, item.to) ? 'page' : undefined}
            onClick={closeMenu}
          >
            <span className="nav-dropdown-item-body">
              <span className="nav-dropdown-item-label">{item.label}</span>
              <span className="nav-dropdown-item-desc">{item.description}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
